import { arrayToById, objectToById, arrayToByForeignId, objectToByForeignId, mergeByForeignId } from "redux/normalize";
import { SavedSearch, NewSavedSearch } from "model/saved-search";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { loginReset } from "redux/slice/authentication";
import { SavedSearchResource } from "api/resource/saved-search";

export interface SavedSearchesState {
	loading?: boolean;
	error?: string | null;
	byId: {
		[key: number]: SavedSearchResource;
	};
	byLeadId: {
		[key: number]: number[];
	};
};

const initialState: SavedSearchesState = {
	byId: {},
	byLeadId: {},
};

const fetchSavedSearches = createAsyncThunk("savedSearches/fetchSavedSearches", async (payload, thunkAPI) => {
	thunkAPI.dispatch(savedSearchesLoading());
	const result = api.getSavedSearches();
	result
		.then((savedSearches) => thunkAPI.dispatch(savedSearchesLoaded(savedSearches)))
		.catch(error => thunkAPI.dispatch(savedSearchesFailed(error)));
	return result;
});

const createSavedSearch = createAsyncThunk("savedSearches/createSavedSearch", async (payload: { savedSearch: NewSavedSearch }, thunkAPI) => {
	thunkAPI.dispatch(savedSearchesLoading());
	const result = api.createSavedSearch(payload.savedSearch);
	result
		.then((savedSearch) => {
			thunkAPI.dispatch(savedSearchLoaded(savedSearch));
			if (!savedSearch.resultsUrl) {
				thunkAPI.dispatch(fetchSavedSearches());
			}
		})
		.catch(error => thunkAPI.dispatch(savedSearchesFailed(error)));
	return result;
});

const updateSavedSearch = createAsyncThunk("savedSearches/updateSavedSearch", async (payload: { savedSearch: SavedSearch }, thunkAPI) => {
	thunkAPI.dispatch(savedSearchesLoading());
	const result = api.updateSavedSearch(payload.savedSearch);
	result
		.then((savedSearch) => thunkAPI.dispatch(savedSearchLoaded(savedSearch)))
		.catch(error => thunkAPI.dispatch(savedSearchesFailed(error)));
	return result;
});

export const savedSearchesSlice = createSlice({
	name: "savedSearches",
	initialState,
	reducers: {
		savedSearchesLoading: (state) => {
			state.loading = true;
		},
		savedSearchesLoaded: (state, action: PayloadAction<SavedSearchResource[]>) => {
			state.loading = false;
			state.error = null;
			state.byId = arrayToById(action.payload, "id");
			state.byLeadId = arrayToByForeignId(action.payload, "id", "leadId");
		},
		savedSearchLoaded: (state, action: PayloadAction<SavedSearchResource>) => {
			const byId = objectToById(action.payload, "id");
			const byLeadId = mergeByForeignId(state.byLeadId, objectToByForeignId(action.payload, "id", "leadId"));
			state.loading = false;
			state.error = null;
			state.byId = {
				...state.byId,
				...byId,
			};
			state.byLeadId = byLeadId;
		},
		savedSearchesFailed: (state, action: PayloadAction<string>) => {
			state.loading = false;
			state.error = action.payload;
		},
		default: (state) => {
			return state;
		}
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const { savedSearchesLoading, savedSearchesLoaded, savedSearchLoaded, savedSearchesFailed } = savedSearchesSlice.actions;
export { fetchSavedSearches, createSavedSearch, updateSavedSearch };
